/**
 * Pure helpers to build the WhatsApp link the funnel opens.
 *
 * WhatsApp hands the agent no session variables (unlike LiveChat), so the
 * pre-filled message is the only context that reaches them. The template is
 * operator-editable, which is why the substitution lives here, testable on its
 * own, instead of inline in a component.
 */

/** Placeholder syntax: `{variable}` or `{variable|fallback when empty}`. */
const PLACEHOLDER_PATTERN = /\{([^{}]*)\}/g;

export interface WhatsAppMessageVars {
  /** Human-readable current step, e.g. "paso 3 de 14 — Raza". */
  paso?: string;
  nombre?: string;
  especie?: string;
}

/**
 * Replace the template placeholders with the session's values.
 *
 * A variable with no value falls back to the text after `|`, or to nothing —
 * in which case the leftover double spaces are collapsed so the customer never
 * sends a message with a visible hole in it.
 */
export function buildWhatsAppMessage(template: string, vars: WhatsAppMessageVars): string {
  if (!template) return '';
  const resolved = template.replace(PLACEHOLDER_PATTERN, (_match, raw: string) => {
    const [name, ...fallbackParts] = raw.split('|');
    const key = name.trim() as keyof WhatsAppMessageVars;
    const value = vars[key];
    if (value && value.trim()) return value.trim();
    return fallbackParts.join('|').trim();
  });
  return resolved.replace(/\s{2,}/g, ' ').trim();
}

/**
 * Build the wa.me URL. Returns an empty string when there is no phone, so
 * callers render no link at all rather than a broken one — the phone is
 * operator-configured and may legitimately be unset.
 */
export function buildWhatsAppHref(phone: string, message: string): string {
  const digits = (phone || '').trim();
  if (!digits) return '';
  const base = `https://wa.me/${digits}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
}
